import overlap from 'react-big-calendar/lib/utils/layout-algorithms/overlap';
import { CalendarEvent } from '../components/ResaCalendar';

function getMaxIdxDFS<T>(
	node: StyledEvent<T>,
	maxIdx: number,
	visited: StyledEvent<T>[],
) {
	for (let i = 0; i < node.friends.length; ++i) {
		if (visited.indexOf(node.friends[i]) > -1) continue;
		const iidx = node?.friends[i]?.idx;
		maxIdx = iidx == null || maxIdx > iidx ? maxIdx : iidx;
		// TODO : trace it by not object but kinda index or something for performance
		visited.push(node.friends[i]);
		const newIdx = getMaxIdxDFS(node.friends[i], maxIdx, visited);
		maxIdx = maxIdx > newIdx ? maxIdx : newIdx;
	}
	return maxIdx;
}

const sortByImportance = (
	a: StyledEvent<CalendarEvent>,
	b: StyledEvent<CalendarEvent>,
) => {
	if (a.event.type === 'mandatory') {
		if (b.event.type === 'mandatory') {
			return sortBySize(a, b);
		}
		return 1;
	} else if (b.event.type === 'mandatory') {
		return -1;
	}
	if (a.event.resource.anonymized === true) {
		if (b.event.resource.anonymized === true) {
			return sortBySize(a, b);
		} else return -1;
	} else if (b.event.resource.anonymized === true) {
		return 1;
	}
	if (a.event.resource.updated == null) {
		if (b.event.resource.updated === null) {
			return sortBySize(a, b);
		} else return -1;
	} else if (b.event.resource.updated == null) {
		return -1;
	}
	return (
		a.event.resource.updated.getTime() - b.event.resource.updated.getTime()
	);
};

export type StyledEvent<T> = {
	event: T;
	style: React.CSSProperties & { xOffset: string };
	friends: StyledEvent<T>[];
	size?: number;
	idx?: number;
};

export function sortBySize<T>(
	{ style: a }: StyledEvent<T>,
	{ style: b }: StyledEvent<T>,
) {
	if (
		typeof a.top !== 'number' ||
		typeof b.top !== 'number' ||
		typeof a.height !== 'number' ||
		typeof b.height !== 'number'
	)
		return 1;
	if (a.top !== b.top) return a.top > b.top ? 1 : -1;
	else if (a.height !== b.height) {
		return a.top + a.height < b.top + b.height ? 1 : -1;
	} else return 0;
}

const customAlgorithm = <T extends CalendarEvent>({
	events,
	minimumStartDifference,
	slotMetrics,
	accessors,
}: {
	events: T[];
	minimumStartDifference: number;
	slotMetrics: any;
	accessors: any;
}) => {
	const styledEvents = overlap({
		events,
		minimumStartDifference,
		slotMetrics,
		accessors,
	}) as StyledEvent<T>[];

	styledEvents.sort(sortBySize);

	for (let i = 0; i < styledEvents.length; ++i) {
		styledEvents[i].friends = [];
		delete styledEvents[i].style.left;
		delete styledEvents[i].style.width;
		delete styledEvents[i].idx;
		delete styledEvents[i].size;
	}

	for (let i = 0; i < styledEvents.length - 1; ++i) {
		const se1 = styledEvents[i];
		const y1 = se1.style.top as number;
		const y2 = (se1.style.top as number) + (se1.style.height as number);

		for (let j = i + 1; j < styledEvents.length; ++j) {
			const se2 = styledEvents[j];
			const y3 = se2.style.top as number;
			const y4 = (se2.style.top as number) + (se2.style.height as number);

			if (
				(y3 >= y1 && y4 <= y2) ||
				(y4 > y1 && y4 <= y2) ||
				(y3 >= y1 && y3 < y2)
			) {
				// TODO : hashmap would be effective for performance
				se1.friends.push(se2);
				se2.friends.push(se1);
			}
		}
	}
	styledEvents.sort(sortByImportance);
	for (let i = 0; i < styledEvents.length; ++i) {
		const se = styledEvents[i];
		const bitmap = [];
		for (let j = 0; j < 100; ++j) bitmap.push(1); // 1 means available

		for (let j = 0; j < se.friends.length; ++j) {
			if (se.friends[j].idx !== undefined) bitmap[se.friends[j].idx!] = 0;
		} // 0 means reserved

		se.idx = bitmap.indexOf(1);
	}

	let minSize = 100;
	for (let i = 0; i < styledEvents.length; ++i) {
		let size = 0;

		if (styledEvents[i].size) continue;

		const allFriends: StyledEvent<T>[] = [];
		const maxIdx = getMaxIdxDFS(styledEvents[i], 0, allFriends);
		size = 100 / (maxIdx + 1);

		if (minSize > size) minSize = size;
		// styledEvents[i].size = size

		// for (let j = 0; j < allFriends.length; ++j) allFriends[j].size = size
	}

	for (let i = 0; i < styledEvents.length; ++i) {
		const e = styledEvents[i];
		e.style.left = (e.idx ?? 0) * minSize;

		// stretch to maximum
		// let maxIdx = 0
		// for (let j = 0; j < e.friends.length; ++j) {
		//   const idx = e.friends[j].idx
		//   maxIdx = maxIdx > idx ? maxIdx : idx
		// }
		// if (maxIdx <= e.idx) e.size = 100 - e.idx * e.size

		// padding between events
		// for this feature, `width` is not percentage based unit anymore
		// it will be used with calc()
		const padding = e.idx === 0 ? 0 : 3;
		e.style.width = `calc(${minSize}% - ${padding}px)`;
		e.style.height = `calc(${e.style.height}% - 2px)`;
		e.style.xOffset = `calc(${e.style.left}% + ${padding}px)`;
	}

	return styledEvents;
};
export default customAlgorithm;
